import "../../containers/cart/cartStyle.scss";
import { Button, Form, Input, Modal, notification, Rate, Tag } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ServiceBase, { checkNetworkConnection } from "utils/ServiceBase";
import {
  pushApiErrorData,
  setApiErrorOpen,
  setOpenNoInternetModal,
} from "redux/action";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as IconWarningDialog } from "assets/icon_warning_dialog.svg";
import { ReactComponent as IconNoInternet } from "assets/icon_no_internet.svg";
import icon_checked_white from "assets/icon_checked_white.png";
import icon_cancel_black from "assets/icon_cancel_black.svg";
import icon_toast_success from "assets/icon_toast_success.png";
import ModalContentV2 from "components/modal/ModalV2";
import ModalLoading from "components/modal/Loading";
import { useTranslation } from "react-i18next";
import ImageComp from "components/image";
import formatNumber from "utils/formatNumber";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { RATE_TIP_AUTO_OPEN_EVENT } from "utils/rateTipAutoOpen";
import {
  normalizeRateTipServiceType,
  shouldAllowRateTipAutoOpen,
} from "utils/rateTipAutoOpenRules";

const MIN_GOOD_STAR = 3;
const STORAGE_PREFIX = "rate-tip:auto-open";
const VERIFY_REQUEST_TTL = 5000;
const pendingVerifyRequests = new Set();
const recentVerifyRequests = new Map();

const cleanupRecentVerifyRequests = (now = Date.now()) => {
  recentVerifyRequests.forEach((timestamp, requestKey) => {
    if (now - timestamp >= VERIFY_REQUEST_TTL) {
      recentVerifyRequests.delete(requestKey);
    }
  });
};

const parseTipOptions = (responseData) => {
  const highTip = responseData["high-tip-amount"];
  return (
    responseData["tip-options"]?.map((tip) => ({
      tip,
      isHighlight: tip === highTip,
    })) || []
  );
};

const ModalRateTip = ({ className }) => {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();
  const location = useLocation();
  const [form] = Form.useForm();

  const currentRateStars = Form.useWatch("rateStars", form);
  const tipSelected = Form.useWatch("tipRadio", form);
  const tipInputVal = Form.useWatch("tipInput", form);

  const isOpenModalNoInternet = useSelector(
    (state) => state.rootReducer.isOpenModalNoInternet,
  );
  const homeBrand = useSelector((state) => state.rootReducer.homeBrand);
  const infoUserTgs = useSelector((state) => state.rootReducer.infoUserTgs);
  const orderSuccess = useSelector((state) => state.rootReducer.orderSuccess);
  const paramConfig = useSelector((state) => state.rootReducer.paramConfig);

  const homeBrandRef = useRef(homeBrand);
  const rateTipContextKeyRef = useRef("");
  const rateTipContextLoadingRef = useRef(false);

  const [isShowModalError, setShowModalError] = useState([false, ""]);
  const [isShowModalNoInternet, setShowModalNoInternet] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [tipOptions, setTipOptions] = useState([]);
  const [rateTipInfo, setRateTipInfo] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const getStorageKey = useCallback(() => {
    return `${STORAGE_PREFIX}:${homeBrandRef.current?.qrCode || "unknown"}`;
  }, []);

  const readAutoOpenState = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(getStorageKey());
      if (!raw) {
        return {
          openCount: 0,
          lastOpenAt: 0,
          completed: false,
          paymentFlowEntered: false,
          firstOrderAt: 0,
        };
      }
      return {
        openCount: 0,
        lastOpenAt: 0,
        completed: false,
        paymentFlowEntered: false,
        firstOrderAt: 0,
        ...JSON.parse(raw),
      };
    } catch (error) {
      return {
        openCount: 0,
        lastOpenAt: 0,
        completed: false,
        paymentFlowEntered: false,
        firstOrderAt: 0,
      };
    }
  }, [getStorageKey]);

  const writeAutoOpenState = useCallback(
    (patch) => {
      const nextState = {
        ...readAutoOpenState(),
        ...patch,
      };
      sessionStorage.setItem(getStorageKey(), JSON.stringify(nextState));
      return nextState;
    },
    [getStorageKey, readAutoOpenState],
  );

  const isRatingCompleted = useCallback(() => {
    return Boolean(
      readAutoOpenState().completed ||
      orderSuccess?.["rating-history"]?.length > 0,
    );
  }, [orderSuccess, readAutoOpenState]);

  const handleRadioClick = (value) => {
    if (tipSelected === value) {
      form.setFieldsValue({ tipRadio: null });
    } else {
      form.setFieldsValue({ tipRadio: value, tipInput: null });
    }
  };

  const handleCheckNetworkConnect = useCallback(() => {
    if (checkNetworkConnection()) return true;
    dispatch(setOpenNoInternetModal(true));
    return false;
  }, [dispatch]);
  const callAPI = useCallback(
    async (api, handleSuccessFunc, setLoading) => {
      if (!handleCheckNetworkConnect()) return;
      setLoading?.(true);
      const { method, url, data } = api;
      const response = await ServiceBase.requestJson({
        method,
        url,
        data,
      });
      setLoading?.(false);
      if (response?.value?.code === "200") {
        if (response?.value?.data) {
          handleSuccessFunc?.(response?.value?.data);
        }
      } else {
        if (response?.code === "ECONNABORTED") {
          dispatch(pushApiErrorData([{ url, time: Date.now() }]));
          notification["error"]({
            key: "internalServerError",
            message: t("internalServerError"),
            duration: 15,
            description: (
              <span
                style={{ color: "#2083DA" }}
                onClick={() => {
                  notification.close("internalServerError");
                  dispatch(setApiErrorOpen(true));
                }}
              >
                {t("forDev")}
              </span>
            ),
          });
        } else {
          if (checkNetworkConnection()) {
            setShowModalError([
              true,
              response?.value?.mess || response?.value?.message,
            ]);
          } else {
            setShowModalNoInternet(true);
          }
        }
      }
    },
    [dispatch, handleCheckNetworkConnect, t],
  );

  const getRateTipInfo = useCallback(() => {
    const qrCode = homeBrandRef.current?.qrCode;
    const serviceType = normalizeRateTipServiceType(homeBrandRef.current?.type);
    const contextKey = `${qrCode || ""}:${serviceType || ""}`;

    if (!qrCode || !contextKey) return;
    if (rateTipContextLoadingRef.current) return;
    if (rateTipContextKeyRef.current === contextKey) return;

    rateTipContextLoadingRef.current = true;
    const param = {
      method: "POST",
      url: "/ggg/api/v4.0/qr-order/rate-tip/context",
      data: {
        guid: `{${qrCode}}`,
      },
    };
    callAPI(
      param,
      (responseData) => {
        rateTipContextKeyRef.current = contextKey;
        setRateTipInfo(responseData);
        setTipOptions(parseTipOptions(responseData));
      },
      undefined,
    ).finally(() => {
      rateTipContextLoadingRef.current = false;
    });
  }, [callAPI]);

  const handleSubmitForm = (values) => {
    const tipAmount = values.tipRadio || values.tipInput;
    const params = {
      method: "POST",
      url: "/ggg/api/v4.0/qr-order/rate-tip/submit",
      data: {
        guid: `{${homeBrandRef.current?.qrCode}}`,
        "rating-point": values.rateStars,
        "rating-note": null,
        "tip-amount":
          Number(String(tipAmount || "").replace(/\D/g, "")) || null,
        "customer-name": infoUserTgs
          ? `${infoUserTgs?.["first-name"]} ${infoUserTgs?.["last-name"]}`
          : null,
        "customer-cellphone": infoUserTgs?.["cellphone"] || null,
        "clm-customer-number": infoUserTgs?.["customer-number"] || null,
      },
    };
    callAPI(
      params,
      () => {
        writeAutoOpenState({ completed: true });
        toast(
          <span className="font-500 toast-text">
            {" "}
            {t("notifyContentSubmitRatingSuccess")}{" "}
          </span>,
          {
            className: "custom-toast-success",
            position: "top-right",
            icon: () => <img width={26} src={icon_toast_success} alt="icon" />,
            autoClose: 1000,
          },
        );
        handleCloseModal();
      },
      setIsLoading,
    );
  };

  const markOpenedModal = useCallback(() => {
    if (homeBrandRef.current) {
      const params = {
        method: "POST",
        url: "/ggg/api/v4.0/qr-order/rate-tip/mark-opened",
        data: {
          guid: `{${homeBrandRef.current.qrCode}}`,
          "service-type": normalizeRateTipServiceType(
            homeBrandRef.current?.type,
          ),
        },
      };
      callAPI(params);
    }
  }, [callAPI]);

  const openModalIfAllowedByServer = useCallback(
    (requestKey) => {
      if (isOpenModal) return;
      if (!requestKey) return;

      const now = Date.now();
      cleanupRecentVerifyRequests(now);
      if (
        pendingVerifyRequests.has(requestKey) ||
        recentVerifyRequests.has(requestKey)
      ) {
        return;
      }

      pendingVerifyRequests.add(requestKey);

      const params = {
        method: "POST",
        url: "/ggg/api/v4.0/qr-order/rate-tip/verify",
        data: {
          guid: `{${homeBrandRef.current.qrCode}}`,
          "service-type": normalizeRateTipServiceType(
            homeBrandRef.current?.type,
          ),
        },
      };

      callAPI(params, (responseVal) => {
        if (!responseVal?.["should-open"]) return;

        const currentState = readAutoOpenState();
        writeAutoOpenState({
          openCount: currentState.openCount + 1,
          lastOpenAt: Date.now(),
        });
        markOpenedModal();
        setIsOpenModal(true);
      }).finally(() => {
        pendingVerifyRequests.delete(requestKey);
        recentVerifyRequests.set(requestKey, Date.now());
      });
    },
    [
      callAPI,
      isOpenModal,
      markOpenedModal,
      readAutoOpenState,
      writeAutoOpenState,
    ],
  );

  const shouldAllowAutoOpen = useCallback(
    ({ trigger }) => {
      const currentState = readAutoOpenState();
      const result = shouldAllowRateTipAutoOpen({
        trigger,
        qrCode: homeBrandRef.current?.qrCode,
        pathname: location.pathname,
        paramConfig,
        serviceType: normalizeRateTipServiceType(homeBrandRef.current?.type),
        autoOpenState: currentState,
        sessions: [],
        ratingCompleted: isRatingCompleted(),
        paymentFlowBlocked: false,
        now: Date.now(),
      });

      return result.allowed;
    },
    [
      isRatingCompleted,
      location.pathname,
      paramConfig,
      readAutoOpenState,
    ],
  );

  const attemptAutoOpen = useCallback(
      ({ trigger, requestKey } = {}) => {
      if (!shouldAllowAutoOpen({ trigger })) {
        return;
      }

      openModalIfAllowedByServer(requestKey);
    },
    [openModalIfAllowedByServer, shouldAllowAutoOpen],
  );

  const triggerAutoOpenByState = useCallback(
    (detail = {}) => {
      const currentState = readAutoOpenState();
      const requestKey =
        detail.requestKey ||
        [
          "event",
          homeBrandRef.current?.qrCode,
          homeBrandRef.current?.type,
          location.pathname,
          detail.trigger || "unknown",
          currentState.openCount,
          detail.timestamp || "na",
        ].join(":");

      attemptAutoOpen({ trigger: detail.trigger, requestKey });
    },
    [attemptAutoOpen, location.pathname, readAutoOpenState],
  );

  const handleCloseModal = () => {
    setIsOpenModal(false);
    form.resetFields();
  };

  useEffect(() => {
    homeBrandRef.current = homeBrand;
  }, [homeBrand]);

  useEffect(() => {
    rateTipContextKeyRef.current = "";
    rateTipContextLoadingRef.current = false;
    setRateTipInfo({});
    setTipOptions([]);

    if (!homeBrand?.qrCode || !homeBrand?.type) return;

    getRateTipInfo();
  }, [getRateTipInfo, homeBrand?.qrCode, homeBrand?.type]);

  //Xử lý khi người dùng mở lại tab trên Mobile (khi tắt màn hình/chuyển app)
  useEffect(() => {
    const handleAutoOpenTrigger = (event) => {
      triggerAutoOpenByState(event?.detail);
    };

    window.addEventListener(RATE_TIP_AUTO_OPEN_EVENT, handleAutoOpenTrigger);
    return () => {
      window.removeEventListener(
        RATE_TIP_AUTO_OPEN_EVENT,
        handleAutoOpenTrigger,
      );
    };
  }, [triggerAutoOpenByState]);

  return (
    <>
      <Modal
        className={`modal-rate-tip ${className}`}
        open={isOpenModal}
        footer={null}
        zIndex={1100}
        centered
        destroyOnClose
        onCancel={() => {
          handleCloseModal();
        }}
      >
        <Form form={form} onFinish={handleSubmitForm}>
          <div className="flex flex-col items-center ">
            <div className="staff-avatar mb-3">
              <ImageComp
                src={rateTipInfo["avatar-url"]}
                isRating={true}
                alt="staff-avatar"
              />
            </div>
            <div className="staff-name mb-3">
              <span>{rateTipInfo["target-name"]}</span>
            </div>
          </div>
          <p className="text-center mb-1">
            {t("rateStarText", { name: rateTipInfo["target-name"] })}
          </p>
          <div className="flex justify-center mb-2">
            <Form.Item name="rateStars" shouldUpdate>
              <Rate className="star-svg" allowClear={false} />
            </Form.Item>
          </div>
          {currentRateStars >= MIN_GOOD_STAR && (
            <div className="tip-session mb-4">
              <h3 className="text-center mb-3">
                {t("tipSentTo") + " " + rateTipInfo["target-name"]}
              </h3>
              <Form.Item name="tipRadio">
                <div className="grid grid-cols-2 tip-list">
                  {tipOptions?.map(({ tip, isHighlight }) => {
                    const isSelected = tipSelected === tip;
                    return (
                      <div
                        key={tip}
                        className={`relative tip-box ${
                          isSelected ? "tip-box--selected" : ""
                        }`}
                        onClick={() => handleRadioClick(tip)}
                      >
                        <span>{`+${formatNumber(tip)}đ`}</span>
                        {isHighlight && (
                          <Tag
                            color="#47C751"
                            className="absolute highlight-tag"
                          >
                            {t("topTipper")}
                          </Tag>
                        )}
                        {isSelected && (
                          <>
                            <div className="bgr-selected"></div>
                            <img
                              src={icon_checked_white}
                              alt="icon"
                              className="icon-selected"
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Form.Item>
              <Form.Item
                name="tipInput"
                className="tip-input"
                getValueFromEvent={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                }}
              >
                <Input
                  style={{ width: "100%" }}
                  placeholder={t("orEnterTip")}
                  disabled={!!tipSelected}
                  allowClear={{
                    clearIcon: (
                      <img src={icon_cancel_black} alt="x" width="20px" />
                    ),
                  }}
                />
              </Form.Item>
            </div>
          )}
          <Form.Item>
            <Button
              htmlType="submit"
              block
              className="rate-tip-btn text-center"
              disabled={!currentRateStars}
            >
              {tipSelected || tipInputVal ? (
                <span className="flex-imp justify-between w-full">
                  <span>{t("submitWithTip")}</span>
                  <span>{`${formatNumber(tipSelected || tipInputVal)}đ`}</span>
                </span>
              ) : (
                <span>{t("submitConfirm")}</span>
              )}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <ModalContentV2
        icon={<IconWarningDialog />}
        modalIsOpen={isShowModalError?.[0]}
        zIndex={200002}
        title={t("errorTitle")}
        titleByCash={""}
        titleConfirm={t("close")}
        isShowBtnCancel={false}
        onConfirm={() => {
          setShowModalError([false, ""]);
        }}
        content={t("contentErrorTimeOut")}
        error={isShowModalError?.[1]}
      />
      <ModalContentV2
        icon={<IconNoInternet />}
        modalIsOpen={isShowModalNoInternet && !isOpenModalNoInternet}
        title={t("textTitleNoInternet")}
        titleConfirm={t("textReload")}
        isShowBtnCancel={false}
        onConfirm={() => {
          setShowModalNoInternet(false);
          window.location.reload();
        }}
        content={t("textContentNoInternet")}
        error={""}
      />
      {isLoading && <ModalLoading />}
    </>
  );
};

export default styled(ModalRateTip)`
  .ant-modal-content {
    margin: 0;

    .ant-modal-close-x {
      color: #1a1a1a;
      font-weight: 700;
    }
  }
  .ant-modal-body {
    padding: 16px 12px;
    color: #1a1a1a;
  }
  .ant-form-item {
    margin-bottom: 0;

    .ant-input-affix-wrapper {
      border-color: #d9d9d9 !important;
      border-radius: 6px;
      line-height: 20px;
      color: #1a1a1a;
      padding: 12px;

      &:focus,
      &.ant-input-affix-wrapper-focused {
        box-shadow: none !important;
      }
    }
  }
  .staff-avatar {
    width: 110px;
    height: 110px;
    border: 1px solid #b98803;
    border-radius: 50%;
    overflow: hidden;
    object-fit: contain;

    img {
      width: 100%;
    }
  }
  .staff-name {
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    font-family: "Manrope-Bold";
  }
  .star-svg {
    font-size: 30px;
    color: #dbac17;

    div[aria-checked="false"] {
      .ant-rate-star-first,
      .ant-rate-star-second {
        color: #e6e6e6;
      }
    }

    .ant-rate-star:not(:last-child) {
      margin-right: 17px;
    }
  }
  .tip-session {
    background-color: #fef5e3;
    padding: 12px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    font-family: "Manrope-Bold";
  }
  .tip-list {
    margin-top: 12px;
    gap: 24px 12px;
    flex-wrap: wrap;

    .tip-box {
      background-color: #fff;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      padding: 12px;
      text-align: center;
      height: auto;
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;

      &--selected {
        border-color: #1a1a1a;
      }
    }
    .highlight-tag {
      font-size: 11px;
      line-height: 18px;
      font-weight: 700;
      padding: 3px 12px;
      border-radius: 99999px;
      top: -18px;
      left: 50%;
      transform: translateX(-50%);
    }
  }
  .bgr-selected {
    position: absolute;
    top: 0;
    right: 0;
    border-top: 11px solid #1a1a1a;
    border-right: 11px solid #1a1a1a;
    border-left: 11px solid transparent;
    border-bottom: 11px solid transparent;
    border-top-right-radius: 4px;
  }
  .icon-selected {
    position: absolute;
    top: 4px;
    right: 3px;
    width: 8px;
  }
  .tip-input {
    margin-top: 24px;

    .ant-input {
      height: auto;
      &::placeholder {
        font-weight: 400;
        font-family: "Manrope", sans-serif;
      }
      &:not(.ant-input-disabled)::placeholder {
        color: #4d4d4d !important;
        opacity: 1;
      }
    }
  }
  .rate-tip-btn {
    background-color: #1a1a1a;
    padding: 12px;
    color: #fff;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    border-radius: 4px;
    height: auto;

    &[disabled] {
      background-color: #cccccc;
    }

    .flex-imp {
      display: flex !important;
    }
  }
`;
